<template>
  <Teleport to="body">
    <Transition name="dist-modal-fade">
      <div
        v-if="product"
        class="dist-detail-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @click.self="emit('close')"
      >
        <div class="dist-detail-modal" ref="modalRef" tabindex="-1">
          <header class="dist-detail-modal__header">
            <div class="dist-detail-modal__header-text">
              <h2 :id="titleId" class="dist-detail-modal__title">{{ product.name }}</h2>
              <p v-if="product.genotype" class="dist-detail-modal__subtitle">{{ product.genotype }}</p>
            </div>
            <button
              type="button"
              class="dist-detail-modal__close"
              aria-label="Cerrar"
              @click="emit('close')"
            >
              <i class="fas fa-times"></i>
            </button>
          </header>

          <div class="dist-detail-modal__body">
            <div class="dist-detail-layout">
              <aside class="dist-detail-gallery">
                <div class="dist-detail-gallery__main">
                  <img
                    v-if="currentImage"
                    :src="currentImage"
                    :alt="product.name"
                    class="dist-detail-gallery__img"
                    @error="onImageError"
                  />
                  <div v-else class="dist-detail-gallery__ph">
                    <i class="fas fa-seedling"></i>
                  </div>
                </div>
                <div v-if="images.length > 1" class="dist-detail-gallery__thumbs">
                  <button
                    v-for="(img, idx) in images"
                    :key="idx"
                    type="button"
                    class="dist-detail-thumb"
                    :class="{ active: imageIndex === idx }"
                    :aria-label="`Imagen ${idx + 1}`"
                    @click="imageIndex = idx"
                  >
                    <img :src="img" alt="" @error="onThumbnailError(img, $event)" />
                  </button>
                </div>
              </aside>

              <div class="dist-detail-main">
                <div class="dist-detail-tags">
                  <span class="dist-detail-tag">{{ distributorTypeLabel(product.type) }}</span>
                  <span v-if="product.limited_edition" class="dist-detail-tag dist-detail-tag--accent">
                    Edición limitada
                  </span>
                  <span v-if="product.highlighted" class="dist-detail-tag dist-detail-tag--muted">
                    Destacado
                  </span>
                  <span v-if="product.show_on_public_web" class="dist-detail-tag dist-detail-tag--web">
                    <i class="fas fa-globe me-1"></i>En web pública
                  </span>
                </div>

                <p
                  v-if="!hasRichContent && !descriptionHtml"
                  class="dist-detail-empty-hint"
                >
                  <i class="fas fa-info-circle me-2"></i>
                  No hay ficha técnica cargada para este producto. Podés ver las presentaciones y stock abajo.
                </p>

                <div v-if="specRows.length" class="dist-detail-specs">
                  <div v-for="row in specRows" :key="row.label" class="dist-detail-spec">
                    <span class="dist-detail-spec__label">{{ row.label }}</span>
                    <span class="dist-detail-spec__value">{{ row.value }}</span>
                  </div>
                </div>

                <div
                  v-if="descriptionHtml"
                  class="dist-detail-description panel-card"
                  v-html="descriptionHtml"
                />

                <section class="dist-detail-purchase">
                  <h3 class="dist-detail-section-title">
                    <i class="fas fa-shopping-basket me-2"></i>Presentaciones y cantidad
                  </h3>
                  <ul class="dist-detail-blisters">
                    <li
                      v-for="blister in blisterList"
                      :key="blister.id"
                      class="dist-detail-blister"
                      :class="{ 'dist-detail-blister--disabled': !canOrderBlister(blister) }"
                    >
                      <div class="dist-detail-blister__info">
                        <strong>
                          {{ blister.seed_count }}
                          {{ ['auto', 'fem'].includes(product.type) ? 'semillas' : 'unidades' }}
                        </strong>
                        <span v-if="blister.prices_visible !== false" class="dist-detail-blister__price">
                          <span
                            v-if="blister.offer_discount_percent > 0 && blister.list_price"
                            class="dist-detail-blister__price-list"
                          >{{ formatPrice(blister.list_price) }}</span>
                          <span :class="{ 'dist-detail-blister__price--offer': blister.offer_discount_percent > 0 }">
                            {{ formatPrice(blister.final_price) }}
                          </span>
                          <span
                            v-if="blister.offer_discount_percent > 0"
                            class="dist-detail-blister__offer-chip"
                          >−{{ Number(blister.offer_discount_percent) }}% OFF</span>
                        </span>
                        <span v-else class="text-muted small">Precio no visible</span>
                      </div>
                      <div class="dist-detail-blister__actions">
                        <div class="qty-wrapper">
                          <button
                            type="button"
                            class="qty-btn"
                            :disabled="qtyFor(blister) <= 0"
                            @click="changeQty(blister, qtyFor(blister) - 1)"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            class="qty-input"
                            :value="qtyFor(blister)"
                            min="0"
                            :max="blister.stock"
                            :disabled="!canOrderBlister(blister)"
                            @change="onQtyInput(blister, $event)"
                          />
                          <button
                            type="button"
                            class="qty-btn"
                            :disabled="!canOrderBlister(blister)"
                            @click="onIncrement(blister)"
                          >
                            +
                          </button>
                        </div>
                        <transition name="stock-warn">
                          <div v-if="stockWarnings[lineKey(blister)]" class="stock-warning">
                            Sin stock disponible
                          </div>
                        </transition>
                      </div>
                    </li>
                  </ul>
                  <p v-if="lineSubtotal > 0" class="dist-detail-line-total">
                    Subtotal en este producto:
                    <strong>{{ formatPrice(lineSubtotal) }}</strong>
                  </p>
                </section>
              </div>
            </div>
          </div>

          <footer class="dist-detail-modal__footer">
            <a
              v-if="publicUrl"
              :href="publicUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-outline-light dist-detail-footer-btn"
            >
              <i class="fas fa-external-link-alt me-2"></i>Ver en sitio público
            </a>
            <button
              type="button"
              class="btn btn-primary dist-detail-footer-btn dist-detail-footer-btn--primary"
              @click="emit('close')"
            >
              Listo
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, reactive } from 'vue'
import { useImageGallery } from '@/composables/useProductImages'
import { freshAdminImage } from '@/utils/adminFreshness'
import {
  distributorTypeLabel,
  distributorLineKey,
  getProductImages,
  getPublicProductUrl,
  getProductSpecRows,
  productHasRichContent,
  sortedBlisters,
} from '@/utils/distributorProduct'

const props = defineProps({
  product: { type: Object, default: null },
  quantities: { type: Object, required: true },
  lineSubtotal: { type: Number, default: 0 },
})

const emit = defineEmits(['close', 'set-qty'])

const modalRef = ref(null)
const titleId = `dist-product-title-${Math.random().toString(36).slice(2, 9)}`

const sourceImages = computed(() => getProductImages(props.product).map(url => freshAdminImage(url, props.product)))
const { images, imageIndex, currentImage, onImageError, onThumbnailError } = useImageGallery(sourceImages)
const specRows = computed(() => getProductSpecRows(props.product))
const hasRichContent = computed(() => productHasRichContent(props.product))
const descriptionHtml = computed(() => {
  const html = props.product?.description
  return html && String(html).trim() ? html : ''
})
const publicUrl = computed(() => getPublicProductUrl(props.product))
const blisterList = computed(() => sortedBlisters(props.product))

const lineKey = (blister) => distributorLineKey(props.product, blister)
const qtyFor = (blister) => props.quantities[lineKey(blister)] || 0

const canOrderBlister = (blister) => {
  const pricesOk = props.product?.prices_visible !== false
  return blister.stock > 0 && pricesOk
}

const formatPrice = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v || 0)

const changeQty = (blister, qty) => {
  emit('set-qty', props.product, blister, qty)
}

const onQtyInput = (blister, event) => {
  changeQty(blister, parseInt(event.target.value, 10) || 0)
}

// Mensaje inline "sin stock" al intentar agregar más de lo disponible
const stockWarnings = reactive({})
const stockWarningTimers = {}

const showStockWarning = (blister) => {
  const k = lineKey(blister)
  stockWarnings[k] = Date.now()
  clearTimeout(stockWarningTimers[k])
  stockWarningTimers[k] = setTimeout(() => {
    delete stockWarnings[k]
    delete stockWarningTimers[k]
  }, 2000)
}

const onIncrement = (blister) => {
  if (qtyFor(blister) >= blister.stock) {
    showStockWarning(blister)
    return
  }
  changeQty(blister, qtyFor(blister) + 1)
}

const onKeydown = (e) => {
  if (e.key === 'Escape' && props.product) emit('close')
}

watch(
  () => props.product?.id,
  () => {
    imageIndex.value = 0
    nextTick(() => modalRef.value?.focus?.())
  }
)

watch(
  () => props.product,
  (p) => {
    document.body.style.overflow = p ? 'hidden' : ''
  }
)

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.dist-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: normal;
}

.dist-detail-overlay :deep(i[class*='fa-']),
.dist-detail-overlay :deep(.fa),
.dist-detail-overlay :deep(.fas),
.dist-detail-overlay :deep(.far),
.dist-detail-overlay :deep(.fab) {
  font-family: 'Font Awesome 6 Free' !important;
  font-style: normal;
}

@media (min-width: 768px) {
  .dist-detail-overlay {
    align-items: center;
    padding: 1.25rem;
  }
}

.dist-detail-modal {
  width: 100%;
  max-height: 96dvh;
  display: flex;
  flex-direction: column;
  background: var(--grey-color, #1e1e1e);
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.12));
  border-radius: 16px 16px 0 0;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45);
  outline: none;
}

@media (min-width: 768px) {
  .dist-detail-modal {
    max-width: 920px;
    max-height: 90dvh;
    border-radius: 16px;
  }
}

.dist-detail-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.dist-detail-modal__title {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
  color: var(--white-color, #fff);
}

.dist-detail-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.55);
}

.dist-detail-modal__close {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;
}

.dist-detail-modal__close:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.3);
}

.dist-detail-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.15rem 1.25rem;
  -webkit-overflow-scrolling: touch;
}

.dist-detail-layout {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

@media (min-width: 768px) {
  .dist-detail-layout {
    flex-direction: row;
    align-items: flex-start;
    gap: 1.5rem;
  }
}

.dist-detail-gallery {
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .dist-detail-gallery {
    width: 38%;
    max-width: 320px;
    position: sticky;
    top: 0;
  }
}

.dist-detail-gallery__main {
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.dist-detail-gallery__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.5rem;
}

.dist-detail-gallery__ph {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: rgba(255, 255, 255, 0.2);
}

.dist-detail-gallery__thumbs {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.65rem;
  flex-wrap: wrap;
}

.dist-detail-thumb {
  width: 52px;
  height: 52px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.dist-detail-thumb.active {
  border-color: var(--primary-color, #ffec00);
}

.dist-detail-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dist-detail-main {
  flex: 1;
  min-width: 0;
}

.dist-detail-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

.dist-detail-tag {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.25rem 0.55rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
}

.dist-detail-tag--accent {
  background: rgba(255, 193, 7, 0.15);
  color: #ffc107;
}

.dist-detail-tag--web {
  background: rgba(255, 236, 0, 0.1);
  color: var(--primary-color, #ffec00);
}

.dist-detail-tag--muted {
  color: rgba(255, 255, 255, 0.6);
}

.dist-detail-empty-hint {
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.55);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  margin-bottom: 1rem;
}

.dist-detail-specs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.dist-detail-spec {
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.dist-detail-spec__label {
  display: block;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 0.15rem;
}

.dist-detail-spec__value {
  font-size: 0.88rem;
  font-weight: 500;
  color: #fff;
  line-height: 1.3;
}

.dist-detail-description {
  padding: 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.92rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
}

.dist-detail-description :deep(p) {
  margin-bottom: 0.5rem;
}

.dist-detail-description :deep(p:last-child) {
  margin-bottom: 0;
}

.dist-detail-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--primary-color, #ffec00);
  margin: 0 0 0.75rem;
}

.dist-detail-blisters {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dist-detail-blister {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.dist-detail-blister--disabled {
  opacity: 0.55;
}

.dist-detail-blister__info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.dist-detail-blister__price {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.dist-detail-blister__price-list {
  color: rgba(255, 255, 255, 0.4);
  text-decoration: line-through;
  font-size: 0.75rem;
}

.dist-detail-blister__price--offer {
  color: var(--primary-color, #ffec00);
  font-weight: 700;
}

.dist-detail-blister__offer-chip {
  font-size: 0.66rem;
  font-weight: 700;
  padding: 0.06rem 0.4rem;
  border-radius: 999px;
  background: #FF7731;
  border: 1px solid #FF7731;
  color: #fff;
}

.dist-detail-blister__stock {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
}

.dist-detail-line-total {
  margin: 0.85rem 0 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

.dist-detail-line-total strong {
  color: var(--primary-color, #ffec00);
}

.qty-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.dist-detail-blister__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.stock-warning {
  font-size: 0.7rem;
  font-weight: 600;
  color: #ff5a5a;
  line-height: 1.2;
  text-align: right;
}

.stock-warn-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.stock-warn-leave-active { transition: opacity 0.4s ease, transform 0.4s ease; }
.stock-warn-enter-from { opacity: 0; transform: translateY(4px); }
.stock-warn-leave-to   { opacity: 0; transform: translateY(-6px); }

.qty-btn {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  border-radius: 8px;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.qty-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.qty-input {
  width: 48px;
  height: 36px;
  text-align: center;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  border-radius: 8px;
  font-size: 0.95rem;
}

.qty-input::-webkit-inner-spin-button {
  display: none;
}

.dist-detail-modal__footer {
  flex-shrink: 0;
  display: flex;
  flex-direction: column-reverse;
  gap: 0.5rem;
  padding: 0.85rem 1.15rem 1rem;
  padding-bottom: max(1rem, env(safe-area-inset-bottom));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
}

@media (min-width: 480px) {
  .dist-detail-modal__footer {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }
}

.dist-detail-footer-btn {
  width: 100%;
  min-height: 2.85rem;
  font-weight: 600;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 480px) {
  .dist-detail-footer-btn {
    width: auto;
    min-width: 10rem;
  }

  .dist-detail-footer-btn--primary {
    min-width: 7rem;
  }
}

.dist-modal-fade-enter-active,
.dist-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dist-modal-fade-enter-active .dist-detail-modal,
.dist-modal-fade-leave-active .dist-detail-modal {
  transition: transform 0.25s ease, opacity 0.2s ease;
}

.dist-modal-fade-enter-from,
.dist-modal-fade-leave-to {
  opacity: 0;
}

.dist-modal-fade-enter-from .dist-detail-modal,
.dist-modal-fade-leave-to .dist-detail-modal {
  transform: translateY(16px);
  opacity: 0.95;
}

@media (min-width: 768px) {
  .dist-modal-fade-enter-from .dist-detail-modal,
  .dist-modal-fade-leave-to .dist-detail-modal {
    transform: translateY(8px) scale(0.98);
  }
}
</style>
